import {
  AuthMethodFragment,
  FeatureAction,
  FeatureContextInput,
  FeatureKey,
  FeaturePermission
} from "@pathwright/graphql-client/src/generated"
import Alert from "@pathwright/ui/src/components/alert/Alert"
import ToggleInput from "@pathwright/ui/src/components/form/form-toggle/ToggleInput"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import View from "@pathwright/ui/src/components/ui/View"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import { Formik } from "formik"
import { useRef } from "react"
import { z } from "zod"
import { toFormikValidate } from "zod-formik-adapter"
import { useSpaceAuthSettings } from "../auth/ManagePathwrightSignInFeature"
import { featurePermissionsQueryPropTypes } from "../propTypes"
import { getFeatureInfo } from "../utils"
import { useFeaturePermission } from "./FeaturePermissionsForm"

function getExternalAuthMethods(authMethods: AuthMethodFragment[]) {
  return authMethods.filter(
    (authMethod) => authMethod.enabled && authMethod.meta.type !== "pathwright"
  )
}

// extending permissions for form
const useFeaturePermissions = ({
  featureKey,
  context
}: {
  featureKey: FeatureKey
  context: FeatureContextInput | null
}) => {
  const permissions = {
    [FeatureAction.AuthLogin]: {
      ...useFeaturePermission({
        featureKey,
        featureAction: FeatureAction.AuthLogin,
        context
      }),
      getInitialValue: function () {
        if (!this.query.data) return undefined
        return (
          this.query.data.contextualFeaturePermission?.permission_in_context ===
          FeaturePermission.Everyone
        )
      },
      getFormValue: function (values: FormValues) {
        return values[this.key]
          ? FeaturePermission.Everyone
          : FeaturePermission.Disabled
      }
    }
  }

  return permissions
}

const FormSchema = z.object({
  [FeatureAction.AuthLogin]: z.boolean()
})

type FormValues = z.infer<typeof FormSchema>

const validator = toFormikValidate(FormSchema)

const FeaturePermissionsAuthLoginForm = ({
  featureKey,
  context
}: {
  featureKey: FeatureKey
  context: FeatureContextInput | null
}) => {
  const formikRef = useRef(null)
  const { t } = useTranslate()
  const { name: featureName } = getFeatureInfo(featureKey, t)
  const permissions = useFeaturePermissions({ featureKey, context })

  const initialValues: FormValues = {
    [FeatureAction.AuthLogin]: permissions[FeatureAction.AuthLogin].getInitialValue()! /* prettier-ignore */
  }

  const spaceAuthSettingsQuery = useSpaceAuthSettings()
  const externalAuthMethods = getExternalAuthMethods(
    spaceAuthSettingsQuery?.data?.space?.authMethods || []
  )
  // You can only disable Pathwright Sign-in when there are other enabled
  // external auth methods.
  const shouldDisableInput =
    externalAuthMethods.length === 1 &&
    initialValues[FeatureAction.AuthLogin] &&
    !spaceAuthSettingsQuery?.data?.space?.pathwright_login_enabled

  const { loading, error } = Object.values(permissions).reduce(
    (state, { query }) => ({
      loading: state.loading || query.loading,
      error: state.error || query.error
    }),
    {} as { loading: boolean; error?: Error }
  )

  if (loading) {
    return <LoadingCircle center />
  }

  if (error) {
    return <Alert error={error} />
  }

  // NOTE: always performing an update mutation even though at times
  // there may not exist a CFP–the backend automatically creates if no CFP exists
  // even when the request is to update an existing CFP
  const handleSubmit = async (values: FormValues) => {
    await Promise.all(
      Object.values(permissions)
        // filter out unchanged permissions
        .filter((config) => values[config.key] !== initialValues[config.key])
        // filter out permissions which user cannot update
        .filter((config) => config.canUpdate)
        .map((config) =>
          config.mutation[0]({
            variables: {
              permissionInContext: config.getFormValue(values)
            }
          })
        )
    )

    await spaceAuthSettingsQuery.refetch()
  }

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      // enableReinitialize allows the form to reset to new state after submission
      enableReinitialize
      validate={validator}
      onSubmit={handleSubmit}
    >
      {(form) => {
        const isDisabled =
          !permissions[FeatureAction.AuthLogin].canUpdate ||
          shouldDisableInput ||
          form.isSubmitting

        return (
          <form style={{ minWidth: "100%" }}>
            <div /* wrapper for View separate prop to work */>
              {!context && (
                <View separate paddingTop paddingBottom>
                  <ToggleInput
                    labelWidth={300}
                    label={t("Enable {{ feature }} Sign In", {
                      feature: featureName
                    })}
                    helperText={t(
                      "Give all members the option to Sign In or Sign Up using their {{ feature }} Account.",
                      {
                        feature: featureName
                      }
                    )}
                    value={form.values[FeatureAction.AuthLogin]}
                    onChange={(value: boolean) => {
                      if (isDisabled) return
                      form.setFieldValue(FeatureAction.AuthLogin, value, false)
                      form.submitForm()
                    }}
                    disabled={isDisabled}
                    alignRight
                  />
                </View>
              )}
            </div>
          </form>
        )
      }}
    </Formik>
  )
}

FeaturePermissionsAuthLoginForm.displayName = "FeaturePermissionsAuthLoginForm"

FeaturePermissionsAuthLoginForm.propTypes = featurePermissionsQueryPropTypes

export default FeaturePermissionsAuthLoginForm
